import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'react-redux';
import logger from 'redux-logger';
import employer from 'src/redux/slices/employerLayout';
import applicantReviewReducer from 'src/redux/slices/applicantReview';
import candidate from 'src/redux/slices/candidateLayout';
import candidateOnboarding from 'src/pages/candidateOnboarding/slices';
import employerOnboarding from 'src/pages/employerOnboarding/slices';
import bulkResumeApplicant from 'src/redux/slices/xray';
import questionHistory from 'src/pages/questionHistory/slice';
import appStateReducer from 'src/redux/slices/appState';
import sharedLayout from 'src/components/SharedLayout/slices';
import resumeBuilder from 'src/pages/ResumeBuilder/slices';
import contribute from './slices/contribute';
import chatReducer from './slices/chat';
import storeReducer from './slices/store';
import inviteApplicantReducer from './slices/inviteApplicant';
import leaderboardReducer from './slices/leaderboard';
import skillBuilderReducer from './slices/skillBuilder';
import adminSlice from './slices/adminLayout';
import config from '../../config';
import candidateProfileReducer from './slices/candidateProfile';
import auth from './slices/auth';
import quizReducer from './slices/quiz';
import competitionReducer from './slices/competition';
import campusAmbassadorReducer from './slices/campusAmbassador';
import codingPracticeReducer from './slices/codingPractice';
import employerCreditReducer from './slices/employerCredit';
import employerFeedbackReducer from './slices/employerFeedback';
import interviewReducer from './slices/interview';
import outreachReducer from './slices/outreachSlice';
import outreachCampaignReducer from './slices/outreachCampaignSlice';
import profileRewardsReducer from './slices/profileRewards';
import submissionTemplateReducer from './slices/submissionTemplate';
import galleryReducer from './slices/gallery';
import careerGuidanceReducer from './slices/careerGuidanceSlice';
import rewardsReducer from './slices/rewards';
import rewardsAdminReducer from './slices/rewardsAdmin';
import assistantReducer from './slices/assistant';
import appApi from './services';
import { profileCompletionListener } from './listeners/profileCompletion';

const reducer = combineReducers({
  auth,
  quiz: quizReducer,
  employer,
  applicantReview: applicantReviewReducer,
  candidate,
  bulkResumeApplicant,
  candidateOnboarding,
  employerOnboarding,
  appState: appStateReducer,
  sharedLayout,
  resumeBuilder,
  contribute,
  questionHistory,
  chat: chatReducer,
  store: storeReducer,
  leaderboard: leaderboardReducer,
  inviteApplicant: inviteApplicantReducer,
  adminSlice,
  competition: competitionReducer,
  skillBuilder: skillBuilderReducer,
  candidateProfile: candidateProfileReducer,
  campusAmbassador: campusAmbassadorReducer,
  codingPractice: codingPracticeReducer,
  employerCredit: employerCreditReducer,
  employerFeedback: employerFeedbackReducer,
  interview: interviewReducer,
  outreach: outreachReducer,
  outreachCampaign: outreachCampaignReducer,
  profileRewards: profileRewardsReducer,
  submissionTemplate: submissionTemplateReducer,
  gallery: galleryReducer,
  careerGuidance: careerGuidanceReducer,
  rewards: rewardsReducer,
  rewardsAdmin: rewardsAdminReducer,
  assistant: assistantReducer,
  [appApi.reducerPath]: appApi.reducer,
});

const store = configureStore({
  reducer,
  middleware: (getDefaultMiddleware) => {
    const middleware = getDefaultMiddleware().prepend(
      profileCompletionListener.middleware,
    );
    return config.ENV === 'development'
      ? middleware.concat(logger, appApi.middleware)
      : middleware.concat(appApi.middleware);
  },
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch = useDispatch.withTypes<AppDispatch>();
export const useAppSelector = useSelector.withTypes<RootState>();

export default store;
